import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LangProvider } from "@/contexts/LangContext";
import { CustomerAuthProvider } from "@/contexts/CustomerAuthContext";
import Layout from "@/components/Layout";
import CustomerAuthCallback from "@/components/CustomerAuthCallback";
import HomePage from "@/pages/HomePage";
import LeistungenPage from "@/pages/LeistungenPage";
import ProduktionPage from "@/pages/ProduktionPage";
import HaeuserPage from "@/pages/HaeuserPage";
import SanierungPage from "@/pages/SanierungPage";
import GrundstueckePage from "@/pages/GrundstueckePage";
import InvestorenPage from "@/pages/InvestorenPage";
import ProjektePage from "@/pages/ProjektePage";
import ProjektDetailPage from "@/pages/ProjektDetailPage";
import UeberUnsPage from "@/pages/UeberUnsPage";
import KontaktPage from "@/pages/KontaktPage";
import MeinKontoPage from "@/pages/MeinKontoPage";
import PartnerBudovaPage from "@/pages/PartnerBudovaPage";
import PartnerRoyalHouseUaPage from "@/pages/PartnerRoyalHouseUaPage";
import PartnerGenericPage from "@/pages/PartnerGenericPage";
import ImpressumPage from "@/pages/ImpressumPage";
import DatenschutzPage from "@/pages/DatenschutzPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import CookieBanner from "@/components/CookieBanner";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

function ProtectedAdmin() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#051630] text-white/60">
        Lade…
      </div>
    );
  }
  if (!user || user.role !== "admin") {
    return <Navigate to="/admin/login" replace />;
  }
  return <Outlet />;
}

// Detect the OAuth return trip (session_id in the URL hash) synchronously
// during render — a useEffect would run too late and cause a race with the
// normal route rendering underneath it.
function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <CustomerAuthCallback />;
  }
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/leistungen" element={<LeistungenPage />} />
        <Route path="/produktion" element={<ProduktionPage />} />
        <Route path="/haeuser" element={<HaeuserPage />} />
        <Route path="/sanierung" element={<SanierungPage />} />
        <Route path="/grundstuecke" element={<GrundstueckePage />} />
        <Route path="/investoren" element={<InvestorenPage />} />
        <Route path="/projekte" element={<ProjektePage />} />
        <Route path="/projekte/:slug" element={<ProjektDetailPage />} />
        <Route path="/ueber-uns" element={<UeberUnsPage />} />
        <Route path="/partner/budova" element={<PartnerBudovaPage />} />
        <Route path="/partner/royalhouse-ua" element={<PartnerRoyalHouseUaPage />} />
        <Route path="/partner/wellbeing" element={<PartnerGenericPage partnerKey="wellbeing" />} />
        <Route path="/partner/ukraine-rebuild" element={<PartnerGenericPage partnerKey="ukraine" />} />
        <Route path="/partner/building-technik" element={<PartnerGenericPage partnerKey="buildingtechnik" />} />
        <Route path="/kontakt" element={<KontaktPage />} />
        <Route path="/konto" element={<MeinKontoPage />} />
        <Route path="/impressum" element={<ImpressumPage />} />
        <Route path="/datenschutz" element={<DatenschutzPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route element={<ProtectedAdmin />}>
          <Route path="/admin" element={<AdminDashboardPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <LangProvider>
        <AuthProvider>
          <BrowserRouter>
            <CustomerAuthProvider>
              <AppRoutes />
              <CookieBanner />
            </CustomerAuthProvider>
          </BrowserRouter>
          <Toaster theme="dark" position="bottom-right" />
        </AuthProvider>
      </LangProvider>
    </HelmetProvider>
  );
}
